// Browser-side observability wiring.
//
// `@aod/observability` is a no-op until SENTRY_DSN or OTEL_EXPORTER_OTLP_ENDPOINT
// is set — callers who haven't opted in pay only the cost of a few null checks.
// We expose a single `setupBrowserObservability()` that:
//   1. Reads Vite-style env vars and hands them to the package explicitly
//      (avoids the `process.env` polyfill question in browser bundles).
//   2. Installs global error handlers so uncaught throws / unhandled promise
//      rejections are captured even before any React error boundary fires.
//   3. Wires `pagehide` to flush pending events before tab close (the
//      browser equivalent of SIGTERM).
//
// Call once from `main.tsx`. App code that wants to capture domain-specific
// errors imports `captureError` from this module (re-exported for convenience).

import { captureError, initObservability, shutdownObservability } from '@aod/observability';

let setupComplete = false;

export async function setupBrowserObservability(): Promise<void> {
  if (setupComplete) return;
  setupComplete = true;

  // Vite inlines `import.meta.env.VITE_*` at build time. We accept
  // `string | undefined` and forward them — observability treats
  // empty/unset as "provider disabled".
  const env = import.meta.env;

  await initObservability({
    appName: 'aod-app',
    appVersion: (env.VITE_APP_VERSION as string | undefined) ?? undefined,
    sentryDsn: (env.VITE_SENTRY_DSN as string | undefined) ?? undefined,
    otelEndpoint: (env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT as string | undefined) ?? undefined,
    environment: env.MODE ?? 'dev',
  }).catch((err) => {
    // Never block startup on observability — match-server uses the same
    // fire-and-forget pattern.
    console.error('[observability] init failed', err);
  });

  // Sync throws from anywhere in the bundle land here. `e.error` is the
  // thrown value when it's an Error; fall back to the message string so
  // Sentry still has something to chew on for `throw 'string'`.
  window.addEventListener('error', (event) => {
    const err = event.error ?? new Error(event.message);
    captureError(err, { phase: 'window.error' });
  });

  // Async rejections (e.g., a Promise from bootSim rejecting after the
  // error boundary would have caught it). Capture the reason — which is
  // usually the real Error object, but might be a plain value.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    captureError(err, { phase: 'unhandledrejection' });
  });

  // Flush pending Sentry/OTEL events before the tab closes. `pagehide`
  // is the modern, reliable equivalent of `beforeunload` and fires in
  // back/forward-cache evictions too. Best-effort — there's no way to
  // await an async flush in `pagehide`, so we just kick it off.
  window.addEventListener('pagehide', () => {
    void shutdownObservability();
  });
}

export { captureError };
